import { Component, ElementRef, HostListener, Inject, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { debounceTime, distinctUntilChanged, forkJoin, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BusinessService } from 'src/app/services/business.service';
import { EventosService } from 'src/app/services/eventos.service';

@Component({
  selector: 'app-select-event-bond',
  templateUrl: './select-event-bond.component.html',
  styleUrls: ['./select-event-bond.component.scss']
})
export class SelectEventBondComponent {

  eventsCompany: any[] = [];
  searchText: string = '';
  allEvents: boolean = false;

  displayedColumns: string[] = ['actions', 'name', 'dates'];
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

  eventSelected: any[] = [];
  page: number = 1;
  finishScroll: boolean = false;
  loaded: boolean = false;
  loadingPage: boolean = true;
  edit: boolean = false;
  object!: any;

  @ViewChild('containerScrollDialog', { static: false }) containerScroll: ElementRef;
  private searchTerms = new Subject<string>();

  constructor(@Inject(MAT_DIALOG_DATA) public data: any, private dialogRef: MatDialogRef<SelectEventBondComponent>,
    private businessService: BusinessService, private eventService: EventosService) { }

  ngOnInit(): void {
    this.getData();
  }

  ngAfterViewInit() {
    if (this.edit) {
      this.data?.object.events.forEach(element => {
        this.changeSelectEvent(element);
      });
    }
    this.searchTerms.pipe(
      debounceTime(500), // Espera 300ms después de cada pulsación de tecla
      distinctUntilChanged(), // Ignora si el nuevo término de búsqueda es igual al anterior
    ).subscribe(items => {
      this.searchText = items;
      this.updateEvents();
    });
  }

  getData() {
    let places = [];
    this.edit = this.data?.edit;
    // Obtener fecha de hoy para filtrar solo eventos futuros
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalizar a inicio del día

    // No pasar toDate porque filtra eventos HASTA esa fecha, no DESDE esa fecha
    this.businessService.getEventsCompanyFilter(this.data.idCompany, this.page, this.searchText, null, places, null, true, null, true, false).subscribe({
      next: (res) => {
        // Filtrar eventos pasados del lado del cliente
        const futureEvents = res.data.data.filter((event: any) => {
          if (event.sessions && event.sessions.length > 0) {
            // Verificar si hay alguna sesión futura
            return event.sessions.some((session: any) => {
              const sessionDate = new Date(session.start_date);
              return sessionDate >= today;
            });
          }
          // Si no tiene sesiones, verificar por end_date o start_date del evento
          if (event.end_date) {
            const endDate = new Date(event.end_date);
            return endDate >= today;
          }
          if (event.start_date) {
            const startDate = new Date(event.start_date);
            return startDate >= today;
          }
          return true; // Si no tiene fechas, incluirlo
        });

        this.eventsCompany = this.eventsCompany.concat(futureEvents);
        if (futureEvents.length <= 0 || futureEvents.length < 15) {
          this.finishScroll = true;
        }

        // Calcular próxima sesión para cada evento
        const sessionCalculations = futureEvents.map(event =>
          this.calculateNextAvailableSession(event)
        );

        // Esperar a que todas las sesiones se calculen
        if (sessionCalculations.length > 0) {
          forkJoin(sessionCalculations).subscribe({
            next: () => {
              this.dataSource = new MatTableDataSource<any>(this.eventsCompany);
              this.loaded = true;
              this.loadingPage = false;
              if (!this.edit) {
                this.selectAll();
              }
            },
            error: (err) => {
              console.error('Error al calcular sesiones:', err);
              this.dataSource = new MatTableDataSource<any>(this.eventsCompany);
              this.loaded = true;
              this.loadingPage = false;
              if (!this.edit) {
                this.selectAll();
              }
            }
          });
        } else {
          this.dataSource = new MatTableDataSource<any>(this.eventsCompany);
          this.loaded = true;
          this.loadingPage = false;
          if (!this.edit) {
            this.selectAll();
          }
        }
      }
    })
  }

  updateEvents() {
    this.page = 1;
    this.loadingPage = true;
    this.finishScroll = false;
    this.eventsCompany = [];
    this.getData();
  }

  selectAll() {
    if (this.allEvents) {
      this.eventSelected = [];
      this.dataSource.data.forEach((event: any) => {
        this.eventSelected.push(event.id);
      });
    }
  }

  changeSelectEvent(event: any) {
    if (this.eventSelected.includes(event.id)) {
      this.eventSelected = this.eventSelected.filter((id: any) => id !== event.id);
    } else {
      this.eventSelected.push(event.id);
    }
    if (this.eventSelected.length == 0) {
      this.allEvents = false;
    }
  }

  searchEvents(event) {
    this.searchTerms.next(event);
  }

  goBackNewBond() {
    this.dialogRef.close({ back: true });
  }

  closeNew() {
    this.dialogRef.close();
  }

  createNewBond() {
    if (this.eventSelected.length > 0) {
      this.dialogRef.close({ object: this.data?.object, events: this.eventSelected, allEvents: this.allEvents });
    }
  }

  calculateNextAvailableSession(event: any) {
    // Si el evento ya tiene sesiones cargadas, usar esas
    if (event.sessions && event.sessions.length > 0) {
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      const futureSessions = event.sessions.filter((session: any) => {
        const sessionDate = new Date(session.start_date);
        sessionDate.setHours(0, 0, 0, 0);
        return sessionDate >= now;
      }).sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());

      if (futureSessions.length > 0) {
        event.next_session = futureSessions[0].start_date;
      }
      return of(event);
    }

    // Si no tiene sesiones cargadas, obtenerlas del API
    return this.eventService.getEventById(event.id).pipe(
      map((res) => {
        const fullEvent = res.data;
        if (fullEvent.sessions && fullEvent.sessions.length > 0) {
          const now = new Date();
          now.setHours(0, 0, 0, 0);

          const futureSessions = fullEvent.sessions.filter((session: any) => {
            const sessionDate = new Date(session.start_date);
            sessionDate.setHours(0, 0, 0, 0);
            return sessionDate >= now;
          }).sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());

          if (futureSessions.length > 0) {
            event.next_session = futureSessions[0].start_date;
          }
        }
        return event;
      }),
      catchError((err) => {
        console.error('Error al obtener sesiones del evento:', event.id, err);
        return of(event);
      })
    );
  }

  //Pagination
  @HostListener('window:scroll', ['$event'])
  @HostListener('scroll', ['$event'])
  public isScrolledInfinityView(event) {
    if (!this.loadingPage) {
      if (!this.finishScroll) {
        const element = this.containerScroll.nativeElement.getBoundingClientRect();
        let bottomShown = element.bottom <= window.innerHeight;
        if (event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight) {
          bottomShown = true;
        }
        if (bottomShown && !this.finishScroll) {
          this.page++;
          this.loadingPage = true;
          setTimeout(() => {
            this.getData();
          }, 200);
        }
        // }
      } else {
        this.finishScroll = true;
      }
    }
  }
}

